import { Typography, useTheme } from '@mui/material';
import { FieldErrors, useFormContext } from 'react-hook-form';

import { EmploymentRiskSectorSelection } from '@/components/Form/Employment/EmploymentRiskSectorSelection';
import EmploymentRiskSectorQuestion from '@/components/Form/Employment/EmploymentRiskyQuestion';
import { RadioToggle } from '@/components/Form/RadioToggle';
import { Select } from '@/components/Form/Select';

import { COUNTRIES_WITH_POPULAR } from '@/constants/countries';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import { EmploymentStatus } from '@/types/user';

import { getProfessionQuestionForSelfEmployed } from '@/utils/employment';
import { convertToBoolean } from '@/utils/string';
import { isGuyanaUser } from '@/utils/user';

import { UpdatePersonalInfo } from '../types';

type SelfEmploymentFormSectionProps = {
  visibleErrors: FieldErrors<UpdatePersonalInfo>;
};

export function SelfEmploymentFormSection({
  visibleErrors,
}: Readonly<SelfEmploymentFormSectionProps>) {
  const theme = useTheme();
  const { user } = useAppSelector(selectUserData);
  const {
    showRiskSectorFlagQuestion,
    showSelfEmployedStartupQuestion,
    showSelfEmployedWithRiskSectorFlagQuestion,
  } = useAppSelector(selectConfigsState);
  const { t, country } = useTranslate();
  const { control, watch } = useFormContext();
  const riskSectorFlag = watch('employmentDetailed.riskSectorFlag');

  const countryOptions = COUNTRIES_WITH_POPULAR.map(item => ({
    ...item,
    text: country[item.value],
  }));

  const isGuyana = isGuyanaUser(user);
  const showSelfEmployedDetails =
    convertToBoolean(riskSectorFlag) === false ||
    showSelfEmployedWithRiskSectorFlagQuestion;

  const renderSelfEmploymentType = () => (
    <>
      <Typography variant="body2Medium">
        {t(
          'updatePersonalInfo.employmentInfoForm.field.selfEmploymentType.label',
        )}
      </Typography>
      <Select
        options={getProfessionQuestionForSelfEmployed(user?.tenant).options.map(
          item => ({
            value: item.value,
            text: t(item.text ?? ''),
          }),
        )}
        control={control}
        name="employmentDetailed.employmentDesc"
        error={!!visibleErrors.employmentDetailed?.employmentDesc}
        helperText={visibleErrors.employmentDetailed?.employmentDesc?.message}
        rules={{
          required: t(
            'updatePersonalInfo.employmentInfoForm.field.selfEmploymentType.error.required',
          ),
        }}
        fullWidth
        useDrawerOnMobile
        withSearchBar
      />
    </>
  );

  const renderStartupQuestion = () => (
    <>
      <Typography variant="body2Medium">
        {t('startUpQuestion.question')}
      </Typography>
      <RadioToggle
        control={control}
        name="employmentDetailed.isStartup"
        sx={{
          '& .MuiFormControlLabel-root': {
            margin: 0,
            flex: 1,
          },
        }}
        options={[
          { value: true, label: t('common.yes') },
          { value: false, label: t('common.no') },
        ]}
      />
    </>
  );

  const renderRiskSectorQuestion = () => (
    <>
      {showRiskSectorFlagQuestion &&
        !showSelfEmployedWithRiskSectorFlagQuestion && (
          <>
            <Typography variant="body2Medium">
              {t(
                'employmentForm.form.employmentRiskSector.SELF_EMPLOYED.question.label',
              )}
            </Typography>
            <Typography variant="caption">
              {t('categoriesQuestion.description')}
            </Typography>
            <RadioToggle
              control={control}
              name="employmentDetailed.riskSectorFlag"
              sx={{
                '& .MuiFormControlLabel-root': {
                  margin: 0,
                  flex: 1,
                },
              }}
              options={[
                { value: true, label: t('common.yes') },
                { value: false, label: t('common.no') },
              ]}
            />
          </>
        )}
      {showSelfEmployedWithRiskSectorFlagQuestion && !isGuyana && (
        <EmploymentRiskSectorQuestion
          control={control}
          name="employmentDetailed.riskSectorFlag"
          employmentStatus={EmploymentStatus.SelfEmployed}
          sx={{
            backgroundColor: theme.palette.background.paper,
            padding: 0,
            '.question-options-container': {
              padding: 0,
            },
            '& .question-title': {
              ...theme.typography.body2Medium,
            },
            '& .MuiFormControlLabel-root': {
              margin: 0,
            },
          }}
          options={[
            { value: true, label: t('common.yes') },
            { value: false, label: t('common.no') },
          ]}
        />
      )}
      {showSelfEmployedWithRiskSectorFlagQuestion && isGuyana && (
        <EmploymentRiskSectorSelection
          control={control}
          name="employmentDetailed.riskSectorCategory"
          employmentStatus={EmploymentStatus.SelfEmployed}
          sx={{
            '.label': {
              ...theme.typography.body2Medium,
            },
          }}
        />
      )}
    </>
  );

  const renderCountryQuestion = () => (
    <>
      <Typography variant="body2Medium">
        {t('selfEmployedForm.form.employerCountry.question')}
      </Typography>
      <Select
        options={countryOptions}
        control={control}
        name="employmentDetailed.employerCountry"
        rules={{
          required: t('selfEmployedForm.form.employerCountry.required'),
        }}
        fullWidth
        label={t('selfEmployedForm.form.employerCountry.label')}
        useDrawerOnMobile
        withSearchBar
      />
    </>
  );
  return (
    <>
      {renderSelfEmploymentType()}

      {showSelfEmployedStartupQuestion && renderStartupQuestion()}

      {showSelfEmployedDetails && renderRiskSectorQuestion()}

      {showSelfEmployedStartupQuestion && renderCountryQuestion()}
    </>
  );
}
